/**
 * Theme preset system. The customizer composes a Theme from 5 picks:
 *   style_id    → radius, letter-spacing, density
 *   base_id     → neutral palette (background, foreground, card, muted, border, …)
 *   accent_id   → primary color (primary, primary-foreground, ring)
 *   heading_id  → font for h1–h6
 *   body_id     → font for body text + sans stack
 * Plus an optional per-token override map for power users.
 */

import type { Theme, ThemeTokens } from './theme';

// ---------- Style presets (radius, letter-spacing) ----------

export type StylePreset = {
  id: string;
  label: string;
  radius: string;
  description?: string;
};

export const STYLE_PRESETS: StylePreset[] = [
  { id: 'vega', label: 'Vega', radius: '0.125rem', description: 'Minimal, keskin' },
  { id: 'nova', label: 'Nova', radius: '0.625rem', description: 'Modern, dengeli' },
  { id: 'maia', label: 'Maia', radius: '1rem', description: 'Yumuşak, davetkar' },
  { id: 'lyra', label: 'Lyra', radius: '1.5rem', description: 'Kapsül görünümü' },
  { id: 'mira', label: 'Mira', radius: '0rem', description: 'Köşeli, brutal' },
  { id: 'luma', label: 'Luma', radius: '0.375rem', description: 'Klasik pixel' },
];

// ---------- Base color palettes (neutrals) ----------
//
// A palette maps 11 stops (50..950) that drive the neutral tokens.
// We pick Tailwind-like scales + a custom Mauve (warm gray with purple).

type Palette11 = {
  '50': string;
  '100': string;
  '200': string;
  '300': string;
  '400': string;
  '500': string;
  '600': string;
  '700': string;
  '800': string;
  '900': string;
  '950': string;
};

export type ColorPreset = {
  id: string;
  label: string;
  swatch: string;            // representative color for UI dot
  palette: Palette11;
};

export const BASE_PRESETS: ColorPreset[] = [
  {
    id: 'stone',
    label: 'Stone',
    swatch: '#78716c',
    palette: {
      '50': '#fafaf9', '100': '#f5f5f4', '200': '#e7e5e4', '300': '#d6d3d1',
      '400': '#a8a29e', '500': '#78716c', '600': '#57534e', '700': '#44403c',
      '800': '#292524', '900': '#1c1917', '950': '#0c0a09',
    },
  },
  {
    id: 'neutral',
    label: 'Neutral',
    swatch: '#737373',
    palette: {
      '50': '#fafafa', '100': '#f5f5f5', '200': '#e5e5e5', '300': '#d4d4d4',
      '400': '#a3a3a3', '500': '#737373', '600': '#525252', '700': '#404040',
      '800': '#262626', '900': '#171717', '950': '#0a0a0a',
    },
  },
  {
    id: 'zinc',
    label: 'Zinc',
    swatch: '#71717a',
    palette: {
      '50': '#fafafa', '100': '#f4f4f5', '200': '#e4e4e7', '300': '#d4d4d8',
      '400': '#a1a1aa', '500': '#71717a', '600': '#52525b', '700': '#3f3f46',
      '800': '#27272a', '900': '#18181b', '950': '#09090b',
    },
  },
  {
    id: 'slate',
    label: 'Slate',
    swatch: '#64748b',
    palette: {
      '50': '#f8fafc', '100': '#f1f5f9', '200': '#e2e8f0', '300': '#cbd5e1',
      '400': '#94a3b8', '500': '#64748b', '600': '#475569', '700': '#334155',
      '800': '#1e293b', '900': '#0f172a', '950': '#020617',
    },
  },
  {
    id: 'gray',
    label: 'Gray',
    swatch: '#6b7280',
    palette: {
      '50': '#f9fafb', '100': '#f3f4f6', '200': '#e5e7eb', '300': '#d1d5db',
      '400': '#9ca3af', '500': '#6b7280', '600': '#4b5563', '700': '#374151',
      '800': '#1f2937', '900': '#111827', '950': '#030712',
    },
  },
  {
    id: 'mauve',
    label: 'Mauve',
    swatch: '#86807d',
    palette: {
      // Radix-inspired warm gray with purple hint
      '50': '#fdfcfc', '100': '#f9f8f8', '200': '#eeedee', '300': '#dfdedf',
      '400': '#c1bfc2', '500': '#86807d', '600': '#655d61', '700': '#504a4e',
      '800': '#36323a', '900': '#211f24', '950': '#121013',
    },
  },
  {
    id: 'sand',
    label: 'Sand',
    swatch: '#898064',
    palette: {
      '50': '#fbf9f4', '100': '#f5f1e5', '200': '#e9e1c8', '300': '#d8caa2',
      '400': '#c2ad79', '500': '#a89362', '600': '#898064', '700': '#6b6248',
      '800': '#443f30', '900': '#2a2720', '950': '#18160f',
    },
  },
];

// ---------- Theme/accent colors ----------

export const ACCENT_PRESETS: ColorPreset[] = [
  {
    id: 'black',
    label: 'Monokrom',
    swatch: '#171717',
    palette: {
      '50': '#f5f5f5', '100': '#e5e5e5', '200': '#d4d4d4', '300': '#a3a3a3',
      '400': '#737373', '500': '#525252', '600': '#404040', '700': '#262626',
      '800': '#171717', '900': '#0a0a0a', '950': '#000000',
    },
  },
  {
    id: 'rose',
    label: 'Rose',
    swatch: '#e11d48',
    palette: {
      '50': '#fff1f2', '100': '#ffe4e6', '200': '#fecdd3', '300': '#fda4af',
      '400': '#fb7185', '500': '#f43f5e', '600': '#e11d48', '700': '#be123c',
      '800': '#9f1239', '900': '#881337', '950': '#4c0519',
    },
  },
  {
    id: 'red',
    label: 'Red',
    swatch: '#dc2626',
    palette: {
      '50': '#fef2f2', '100': '#fee2e2', '200': '#fecaca', '300': '#fca5a5',
      '400': '#f87171', '500': '#ef4444', '600': '#dc2626', '700': '#b91c1c',
      '800': '#991b1b', '900': '#7f1d1d', '950': '#450a0a',
    },
  },
  {
    id: 'orange',
    label: 'Orange',
    swatch: '#ea580c',
    palette: {
      '50': '#fff7ed', '100': '#ffedd5', '200': '#fed7aa', '300': '#fdba74',
      '400': '#fb923c', '500': '#f97316', '600': '#ea580c', '700': '#c2410c',
      '800': '#9a3412', '900': '#7c2d12', '950': '#431407',
    },
  },
  {
    id: 'amber',
    label: 'Amber',
    swatch: '#d97706',
    palette: {
      '50': '#fffbeb', '100': '#fef3c7', '200': '#fde68a', '300': '#fcd34d',
      '400': '#fbbf24', '500': '#f59e0b', '600': '#d97706', '700': '#b45309',
      '800': '#92400e', '900': '#78350f', '950': '#451a03',
    },
  },
  {
    id: 'green',
    label: 'Green',
    swatch: '#16a34a',
    palette: {
      '50': '#f0fdf4', '100': '#dcfce7', '200': '#bbf7d0', '300': '#86efac',
      '400': '#4ade80', '500': '#22c55e', '600': '#16a34a', '700': '#15803d',
      '800': '#166534', '900': '#14532d', '950': '#052e16',
    },
  },
  {
    id: 'teal',
    label: 'Teal',
    swatch: '#0d9488',
    palette: {
      '50': '#f0fdfa', '100': '#ccfbf1', '200': '#99f6e4', '300': '#5eead4',
      '400': '#2dd4bf', '500': '#14b8a6', '600': '#0d9488', '700': '#0f766e',
      '800': '#115e59', '900': '#134e4a', '950': '#042f2e',
    },
  },
  {
    id: 'blue',
    label: 'Blue',
    swatch: '#2563eb',
    palette: {
      '50': '#eff6ff', '100': '#dbeafe', '200': '#bfdbfe', '300': '#93c5fd',
      '400': '#60a5fa', '500': '#3b82f6', '600': '#2563eb', '700': '#1d4ed8',
      '800': '#1e40af', '900': '#1e3a8a', '950': '#172554',
    },
  },
  {
    id: 'indigo',
    label: 'Indigo',
    swatch: '#4f46e5',
    palette: {
      '50': '#eef2ff', '100': '#e0e7ff', '200': '#c7d2fe', '300': '#a5b4fc',
      '400': '#818cf8', '500': '#6366f1', '600': '#4f46e5', '700': '#4338ca',
      '800': '#3730a3', '900': '#312e81', '950': '#1e1b4b',
    },
  },
  {
    id: 'violet',
    label: 'Violet',
    swatch: '#7c3aed',
    palette: {
      '50': '#f5f3ff', '100': '#ede9fe', '200': '#ddd6fe', '300': '#c4b5fd',
      '400': '#a78bfa', '500': '#8b5cf6', '600': '#7c3aed', '700': '#6d28d9',
      '800': '#5b21b6', '900': '#4c1d95', '950': '#2e1065',
    },
  },
  {
    id: 'pink',
    label: 'Pink',
    swatch: '#db2777',
    palette: {
      '50': '#fdf2f8', '100': '#fce7f3', '200': '#fbcfe8', '300': '#f9a8d4',
      '400': '#f472b6', '500': '#ec4899', '600': '#db2777', '700': '#be185d',
      '800': '#9d174d', '900': '#831843', '950': '#500724',
    },
  },
];

// ---------- Font presets ----------

export type FontPresetEntry = {
  id: string;
  label: string;
  stack: string;
  googleFamily?: string;   // raw Google Fonts `family=` value, pipe-joined
  aa?: 'serif' | 'sans' | 'mono';
};

export const FONT_CHOICES: FontPresetEntry[] = [
  {
    id: 'geist',
    label: 'Geist',
    stack: "'Geist', ui-sans-serif, system-ui, sans-serif",
    googleFamily: 'Geist:wght@300;400;500;600;700',
    aa: 'sans',
  },
  {
    id: 'inter',
    label: 'Inter',
    stack: "'Inter', ui-sans-serif, system-ui, sans-serif",
    googleFamily: 'Inter:wght@400;500;600;700',
    aa: 'sans',
  },
  {
    id: 'nunito-sans',
    label: 'Nunito Sans',
    stack: "'Nunito Sans', ui-sans-serif, system-ui, sans-serif",
    googleFamily: 'Nunito+Sans:wght@400;600;700;800',
    aa: 'sans',
  },
  {
    id: 'dm-sans',
    label: 'DM Sans',
    stack: "'DM Sans', ui-sans-serif, system-ui, sans-serif",
    googleFamily: 'DM+Sans:wght@400;500;600;700',
    aa: 'sans',
  },
  {
    id: 'instrument-sans',
    label: 'Instrument Sans',
    stack: "'Instrument Sans', ui-sans-serif, system-ui, sans-serif",
    googleFamily: 'Instrument+Sans:wght@400;500;600;700',
    aa: 'sans',
  },
  {
    id: 'space-grotesk',
    label: 'Space Grotesk',
    stack: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
    googleFamily: 'Space+Grotesk:wght@400;500;600;700',
    aa: 'sans',
  },
  {
    id: 'outfit',
    label: 'Outfit',
    stack: "'Outfit', ui-sans-serif, system-ui, sans-serif",
    googleFamily: 'Outfit:wght@400;500;600;700',
    aa: 'sans',
  },
  {
    id: 'playfair',
    label: 'Playfair Display',
    stack: "'Playfair Display', ui-serif, Georgia, serif",
    googleFamily: 'Playfair+Display:wght@400;500;600;700',
    aa: 'serif',
  },
  {
    id: 'merriweather',
    label: 'Merriweather',
    stack: "'Merriweather', ui-serif, Georgia, serif",
    googleFamily: 'Merriweather:wght@400;700',
    aa: 'serif',
  },
  {
    id: 'crimson',
    label: 'Crimson Pro',
    stack: "'Crimson Pro', ui-serif, Georgia, serif",
    googleFamily: 'Crimson+Pro:wght@400;500;600;700',
    aa: 'serif',
  },
  {
    id: 'system',
    label: 'Sistem',
    stack: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
    aa: 'sans',
  },
];

const MONO_STACK = "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

// ---------- Preset IDs type ----------

export type PresetIds = {
  style: string;
  base: string;
  accent: string;
  heading: string;
  body: string;
};

export const DEFAULT_PRESETS: PresetIds = {
  style: 'nova',
  base: 'neutral',
  accent: 'black',
  heading: 'geist',
  body: 'geist',
};

// ---------- Resolve presets to a full Theme ----------

function lookup<T extends { id: string }>(list: T[], id: string, fallback: T): T {
  return list.find((p) => p.id === id) ?? fallback;
}

export function composeTheme(
  ids: PresetIds,
  overrides?: Partial<ThemeTokens>,
  overridesDark?: Partial<ThemeTokens>,
): Theme {
  const style = lookup(STYLE_PRESETS, ids.style, STYLE_PRESETS[1]); // nova
  const base = lookup(BASE_PRESETS, ids.base, BASE_PRESETS[1]); // neutral
  const accent = lookup(ACCENT_PRESETS, ids.accent, ACCENT_PRESETS[0]); // black
  const body = lookup(FONT_CHOICES, ids.body, FONT_CHOICES[0]); // geist
  void ids.heading; // heading handled separately via applyHeadingFont

  const b = base.palette;
  const a = accent.palette;

  const light: ThemeTokens = {
    background: '#ffffff',
    foreground: b['950'],
    card: '#ffffff',
    'card-foreground': b['950'],
    popover: '#ffffff',
    'popover-foreground': b['950'],
    primary: a['800'],
    'primary-foreground': accent.id === 'black' ? '#fafafa' : '#ffffff',
    secondary: b['100'],
    'secondary-foreground': b['900'],
    muted: b['100'],
    'muted-foreground': b['500'],
    accent: b['100'],
    'accent-foreground': b['900'],
    destructive: '#e40014',
    'destructive-foreground': '#fcf3f3',
    border: b['200'],
    input: b['200'],
    ring: a['500'],
    sidebar: b['50'],
    'sidebar-foreground': b['950'],
    'sidebar-primary': a['800'],
    'sidebar-primary-foreground': '#fafafa',
    'sidebar-accent': b['100'],
    'sidebar-accent-foreground': b['900'],
    'sidebar-border': b['200'],
    'sidebar-ring': a['500'],
    ...(overrides ?? {}),
  };

  const dark: ThemeTokens = {
    background: b['950'],
    foreground: b['50'],
    card: b['900'],
    'card-foreground': b['50'],
    popover: b['900'],
    'popover-foreground': b['50'],
    primary: accent.id === 'black' ? b['100'] : a['400'],
    'primary-foreground': accent.id === 'black' ? b['900'] : '#ffffff',
    secondary: b['800'],
    'secondary-foreground': b['50'],
    muted: b['800'],
    'muted-foreground': b['400'],
    accent: b['800'],
    'accent-foreground': b['50'],
    destructive: '#ff6568',
    'destructive-foreground': '#fef2f2',
    border: 'rgba(255,255,255,0.1)',
    input: 'rgba(255,255,255,0.15)',
    ring: a['400'],
    sidebar: b['900'],
    'sidebar-foreground': b['50'],
    'sidebar-primary': accent.id === 'black' ? a['500'] : a['600'],
    'sidebar-primary-foreground': '#ffffff',
    'sidebar-accent': b['800'],
    'sidebar-accent-foreground': b['50'],
    'sidebar-border': 'rgba(255,255,255,0.1)',
    'sidebar-ring': a['500'],
    ...(overridesDark ?? {}),
  };

  return {
    radius: style.radius,
    font_sans: body.stack,
    font_mono: MONO_STACK,
    light,
    dark,
    preset_id: JSON.stringify(ids),
  } as Theme & { heading_font?: string };
}

/** Load all Google font families at once (heading + body). */
export function loadPresetFonts(ids: PresetIds) {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById('cms-hub-preset-fonts');
  if (existing) existing.remove();

  const families: string[] = [];
  const heading = lookup(FONT_CHOICES, ids.heading, FONT_CHOICES[0]);
  const body = lookup(FONT_CHOICES, ids.body, FONT_CHOICES[0]);
  if (heading.googleFamily) families.push(heading.googleFamily);
  if (body.googleFamily && body.googleFamily !== heading.googleFamily) {
    families.push(body.googleFamily);
  }
  // Always keep Geist Mono loaded for code
  families.push('Geist+Mono:wght@400;500;600');

  if (families.length === 0) return;
  const link = document.createElement('link');
  link.id = 'cms-hub-preset-fonts';
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${families.join('&family=')}&display=swap`;
  document.head.appendChild(link);
}

/** Apply heading font separately via a style tag so h1–h6 use it. */
export function applyHeadingFont(ids: PresetIds) {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById('cms-hub-heading-font');
  if (existing) existing.remove();
  const heading = lookup(FONT_CHOICES, ids.heading, FONT_CHOICES[0]);
  if (heading.stack === lookup(FONT_CHOICES, ids.body, FONT_CHOICES[0]).stack) return;
  const style = document.createElement('style');
  style.id = 'cms-hub-heading-font';
  style.textContent = `h1, h2, h3, h4, h5, h6 { font-family: ${heading.stack}; }`;
  document.head.appendChild(style);
}

/** Try to detect preset IDs from a Theme. Falls back to defaults. */
export function detectPresetIds(theme: Theme): PresetIds {
  if (typeof theme.preset_id === 'string' && theme.preset_id.startsWith('{')) {
    try {
      return { ...DEFAULT_PRESETS, ...(JSON.parse(theme.preset_id) as Partial<PresetIds>) };
    } catch {
      /* fall through */
    }
  }
  return DEFAULT_PRESETS;
}
